import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator } from './chunk-MSCJGV3K.js';
import { EmptyState, ErrorState } from './chunk-4PI4D3M6.js';
import { Skeleton, DrilldownPanel } from './chunk-7QAGCS3T.js';
import { Checkbox } from './chunk-KAFWCBYS.js';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './chunk-PEIGN6MO.js';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './chunk-DAJW2H54.js';
import { Separator } from './chunk-NCCYLVD3.js';
import { Popover, PopoverTrigger, PopoverContent } from './chunk-BLADNTOL.js';
import { Input } from './chunk-VYHSTPFS.js';
import { Label } from './chunk-676CI6I4.js';
import { Badge } from './chunk-S775MXMS.js';
import { Button } from './chunk-UDB35RLY.js';
import { cn } from './chunk-FFX3LZW7.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import * as React6 from 'react';
import { useReactTable, getSortedRowModel, getCoreRowModel, flexRender, getPaginationRowModel, getFacetedMinMaxValues, getFacetedUniqueValues, getFacetedRowModel, getFilteredRowModel } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ArrowUp, ArrowDown, ChevronsUpDown, ChevronLeft, ChevronRight, Settings2, GripVertical, PlusCircle, Check, ListFilter, X } from 'lucide-react';
import { useSensors, useSensor, PointerSensor, KeyboardSensor, DndContext, closestCenter } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { fieldLabel, TABLE_DENSITY_ROW_HEIGHT } from '@authe/fe-commons/display';
import { DAY_MS } from '@authe/fe-commons/time';
import { createScopedStorage } from '@authe/fe-commons/storage';

function DataTableColumnHeader({
  title,
  className
}) {
  return /* @__PURE__ */ jsx("span", { className: cn("font-medium", className), children: title });
}
function isNearEnd(lastRenderedIndex, total, threshold) {
  if (total <= 0 || lastRenderedIndex < 0) return false;
  return lastRenderedIndex >= total - 1 - threshold;
}
var FLEX_COLUMN_MIN_WIDTH = 120;
function declaredSizeIds(columns) {
  return new Set(declaredColumnValues(columns, "size").keys());
}
function declaredMinSizes(columns) {
  return declaredColumnValues(columns, "minSize");
}
function declaredColumnValues(columns, key) {
  const values = /* @__PURE__ */ new Map();
  const walk = (defs) => {
    for (const def of defs) {
      const nested = def.columns;
      if (nested) walk(nested);
      const id = def.id ?? def.accessorKey;
      const value = def[key];
      if (value != null && id != null) values.set(String(id), value);
    }
  };
  walk(columns);
  return values;
}
var ROW_OWN_CONTROL_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "summary",
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="combobox"]',
  '[role="separator"]',
  '[contenteditable="true"]'
].join(",");
function isOwnControlEvent(target, row) {
  if (!(target instanceof Element)) return false;
  const control = target.closest(ROW_OWN_CONTROL_SELECTOR);
  return control != null && control !== row && row.contains(control);
}
function isRowActivationKey(key) {
  return key === "Enter" || key === " " || key === "Spacebar";
}
function DataTable({
  data,
  columns,
  table: controlledTable,
  rowHeight = 40,
  className,
  emptyState,
  onRowClick,
  getRowId,
  activeRowId,
  rowClassName,
  onEndReached,
  endReachedThreshold = 10,
  enableColumnResizing = false
}) {
  const [sorting, setSorting] = React6.useState([]);
  const internalTable = useReactTable({
    data: data ?? [],
    columns: columns ?? [],
    state: { sorting },
    onSortingChange: setSorting,
    enableColumnResizing,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel()
  });
  const table = controlledTable ?? internalTable;
  const rows = table.getRowModel().rows;
  const parentRef = React6.useRef(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 12
  });
  const virtualItemsForEnd = virtualizer.getVirtualItems();
  const lastRenderedIndex = virtualItemsForEnd.length ? virtualItemsForEnd[virtualItemsForEnd.length - 1].index : -1;
  React6.useEffect(() => {
    if (!onEndReached || rows.length === 0) return;
    if (isNearEnd(lastRenderedIndex, rows.length, endReachedThreshold)) onEndReached();
  }, [onEndReached, lastRenderedIndex, rows.length, endReachedThreshold]);
  const activeIndex = React6.useMemo(() => {
    if (!activeRowId || !getRowId) return -1;
    return rows.findIndex((row) => getRowId(row.original) === activeRowId);
  }, [activeRowId, getRowId, rows]);
  React6.useEffect(() => {
    if (activeIndex >= 0) virtualizer.scrollToIndex(activeIndex, { align: "center" });
  }, [activeIndex]);
  if (rows.length === 0 && emptyState) {
    return /* @__PURE__ */ jsx("div", { className: cn("flex items-center justify-center p-6", className), children: emptyState });
  }
  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;
  const colSpan = table.getVisibleLeafColumns().length || 1;
  const declared = declaredSizeIds(table.options.columns);
  const minSizes = declaredMinSizes(table.options.columns);
  const hasWidth = (column) => declared.has(column.id) || column.getCanResize();
  const minTableWidth = table.getVisibleLeafColumns().reduce(
    (sum, column) => sum + (hasWidth(column) ? column.getSize() : minSizes.get(column.id) ?? FLEX_COLUMN_MIN_WIDTH),
    0
  );
  return /* @__PURE__ */ jsx("div", { ref: parentRef, className: cn("overflow-auto rounded-md border", className), children: /* @__PURE__ */ jsxs(
    Table,
    {
      className: "w-full text-sm",
      style: { tableLayout: "fixed", borderCollapse: "separate", borderSpacing: 0, minWidth: minTableWidth },
      children: [
        /* @__PURE__ */ jsx(TableHeader, { className: "sticky top-0 z-10 bg-card", children: table.getHeaderGroups().map((headerGroup) => /* @__PURE__ */ jsx(TableRow, { className: "border-b", children: headerGroup.headers.map((header) => {
          const size = header.getSize();
          const canResize = header.column.getCanResize();
          const isResizing = header.column.getIsResizing();
          const canSort = header.column.getCanSort();
          const alignRight = header.column.columnDef.meta?.align === "right";
          const label = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());
          return /* @__PURE__ */ jsxs(
            TableHead,
            {
              "aria-sort": canSort ? ariaSort(header.column) : void 0,
              className: cn(
                "relative select-none truncate px-3 py-2 text-left font-medium text-muted-foreground",
                alignRight && "text-right"
              ),
              style: hasWidth(header.column) ? { width: size } : void 0,
              children: [
                canSort ? /* @__PURE__ */ jsxs(
                  "button",
                  {
                    type: "button",
                    onClick: header.column.getToggleSortingHandler(),
                    title: `Sort by ${sortableTitle(header.column)}`,
                    className: cn(
                      "inline-flex max-w-full items-center gap-1 truncate rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                      alignRight && "flex-row-reverse"
                    ),
                    children: [
                      /* @__PURE__ */ jsx("span", { className: "truncate", children: label }),
                      /* @__PURE__ */ jsx(SortIndicator, { column: header.column })
                    ]
                  }
                ) : label,
                canResize && /* @__PURE__ */ jsx(
                  "span",
                  {
                    role: "separator",
                    "aria-orientation": "vertical",
                    "aria-label": "Resize column",
                    onMouseDown: header.getResizeHandler(),
                    onTouchStart: header.getResizeHandler(),
                    onClick: (event) => event.stopPropagation(),
                    className: cn(
                      "absolute right-0 top-0 h-full w-1 cursor-col-resize touch-none select-none",
                      "bg-border opacity-0 transition-opacity hover:opacity-100",
                      isResizing && "bg-brand opacity-100"
                    )
                  }
                )
              ]
            },
            header.id
          );
        }) }, headerGroup.id)) }),
        /* @__PURE__ */ jsxs(TableBody, { children: [
          paddingTop > 0 && /* @__PURE__ */ jsx(TableRow, { "aria-hidden": true, children: /* @__PURE__ */ jsx(TableCell, { colSpan, style: { height: paddingTop, padding: 0, border: 0 } }) }),
          virtualItems.map((vRow) => {
            const row = rows[vRow.index];
            if (!row) return null;
            const id = getRowId?.(row.original);
            const isActive = activeRowId != null && id === activeRowId;
            return /* @__PURE__ */ jsx(
              TableRow,
              {
                "data-active": isActive || void 0,
                tabIndex: onRowClick ? 0 : void 0,
                "aria-selected": onRowClick ? isActive : void 0,
                onClick: onRowClick ? (event) => {
                  if (isOwnControlEvent(event.target, event.currentTarget)) return;
                  onRowClick(row.original);
                } : void 0,
                onKeyDown: onRowClick ? (event) => {
                  if (event.target !== event.currentTarget) return;
                  if (!isRowActivationKey(event.key)) return;
                  event.preventDefault();
                  onRowClick(row.original);
                } : void 0,
                className: cn(
                  "border-b hover:bg-muted/50",
                  onRowClick && "cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                  isActive && "bg-brand/10 ring-1 ring-inset ring-brand/40",
                  rowClassName?.(row.original)
                ),
                style: { height: rowHeight },
                children: row.getVisibleCells().map((cell) => /* @__PURE__ */ jsx(
                  TableCell,
                  {
                    className: cn(
                      "overflow-hidden px-3 py-2 align-middle",
                      cell.column.columnDef.meta?.align === "right" && "text-right"
                    ),
                    children: flexRender(cell.column.columnDef.cell, cell.getContext())
                  },
                  cell.id
                ))
              },
              row.id
            );
          }),
          paddingBottom > 0 && /* @__PURE__ */ jsx(TableRow, { "aria-hidden": true, children: /* @__PURE__ */ jsx(TableCell, { colSpan, style: { height: paddingBottom, padding: 0, border: 0 } }) })
        ] })
      ]
    }
  ) });
}
function ariaSort(column) {
  const sorted = column.getIsSorted();
  return sorted === "asc" ? "ascending" : sorted === "desc" ? "descending" : "none";
}
function sortableTitle(column) {
  const sorted = column.getIsSorted();
  return sorted === "asc" ? "descending" : sorted === "desc" ? "no order" : "ascending";
}
function SortIndicator({ column }) {
  const sorted = column.getIsSorted();
  if (sorted === "asc") return /* @__PURE__ */ jsx(ArrowUp, { className: "h-3.5 w-3.5 shrink-0 text-foreground" });
  if (sorted === "desc") return /* @__PURE__ */ jsx(ArrowDown, { className: "h-3.5 w-3.5 shrink-0 text-foreground" });
  return /* @__PURE__ */ jsx(ChevronsUpDown, { className: "h-3.5 w-3.5 shrink-0 opacity-40" });
}
function DataTablePagination({
  hasPrev = false,
  hasNext = false,
  onPrev,
  onNext,
  label,
  isLoading = false,
  className
}) {
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between gap-2 py-2", className), children: [
    /* @__PURE__ */ jsx("span", { className: "text-sm text-muted-foreground", children: label }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
      /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", onClick: onPrev, disabled: !hasPrev || isLoading, children: [
        /* @__PURE__ */ jsx(ChevronLeft, { className: "mr-1 h-4 w-4" }),
        "Previous"
      ] }),
      /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", onClick: onNext, disabled: !hasNext || isLoading, children: [
        "Next",
        /* @__PURE__ */ jsx(ChevronRight, { className: "ml-1 h-4 w-4" })
      ] })
    ] })
  ] });
}
function columnLabel(column, labels) {
  return column.columnDef.meta?.label ?? labels?.[column.id] ?? fieldLabel(column.id);
}
function TableColumnSettings({
  table,
  labels,
  triggerLabel = "Columns",
  enableReorder = true,
  defaultVisibility,
  className
}) {
  const [open, setOpen] = React6.useState(false);
  const [draft, setDraft] = React6.useState([]);
  const snapshot = React6.useCallback(() => {
    return table.getAllLeafColumns().map((column) => ({
      id: column.id,
      label: columnLabel(column, labels),
      visible: column.getIsVisible(),
      locked: column.columnDef.meta?.lockPosition === true,
      alwaysVisible: !column.getCanHide()
    }));
  }, [table, labels]);
  const onOpenChange = (next) => {
    if (next) setDraft(snapshot());
    setOpen(next);
  };
  const sensors = useSensors(
    // A few pixels of travel before a drag starts, or the pointer-down that
    // ticks a checkbox is read as the beginning of a drag and the tick is lost.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const movable = draft.filter((entry) => !entry.locked);
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setDraft((current) => {
      const locked = current.filter((entry) => entry.locked);
      const rest = current.filter((entry) => !entry.locked);
      const ids = rest.map((entry) => entry.id);
      const from = ids.indexOf(String(active.id));
      const to = ids.indexOf(String(over.id));
      if (from < 0 || to < 0) return current;
      return [...locked, ...arrayMove(rest, from, to)];
    });
  };
  const setVisible = (id, visible) => {
    setDraft(
      (current) => current.map((entry) => entry.id === id ? { ...entry, visible } : entry)
    );
  };
  const apply = () => {
    if (enableReorder) table.setColumnOrder(draft.map((entry) => entry.id));
    const visibility = {};
    for (const entry of draft) visibility[entry.id] = entry.visible;
    table.setColumnVisibility(visibility);
    setOpen(false);
  };
  const reset = () => {
    table.setColumnOrder([]);
    table.setColumnVisibility(defaultVisibility ?? {});
    setOpen(false);
  };
  const shown = draft.filter((entry) => entry.visible).length;
  return /* @__PURE__ */ jsxs(Dialog, { open, onOpenChange, children: [
    /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: cn("ml-auto h-8", className), children: [
      /* @__PURE__ */ jsx(Settings2, { className: "mr-2 h-4 w-4" }),
      triggerLabel
    ] }) }),
    /* @__PURE__ */ jsxs(DialogContent, { className: "max-w-md", children: [
      /* @__PURE__ */ jsxs(DialogHeader, { children: [
        /* @__PURE__ */ jsx(DialogTitle, { children: "Columns" }),
        /* @__PURE__ */ jsx(DialogDescription, { children: enableReorder ? "Choose which columns to show, and drag to reorder them." : "Choose which columns to show." })
      ] }),
      /* @__PURE__ */ jsx(DndContext, { sensors, collisionDetection: closestCenter, onDragEnd, children: /* @__PURE__ */ jsx(
        SortableContext,
        {
          items: movable.map((entry) => entry.id),
          strategy: verticalListSortingStrategy,
          children: /* @__PURE__ */ jsx("ul", { className: "-mx-1 max-h-[45vh] space-y-0.5 overflow-y-auto px-1", children: draft.map((entry) => /* @__PURE__ */ jsx(
            ColumnSettingsRow,
            {
              entry,
              draggable: enableReorder && !entry.locked,
              onVisibleChange: (visible) => setVisible(entry.id, visible)
            },
            entry.id
          )) })
        }
      ) }),
      /* @__PURE__ */ jsxs(DialogFooter, { className: "items-center gap-2 sm:justify-between", children: [
        /* @__PURE__ */ jsxs("span", { className: "text-xs text-muted-foreground", children: [
          shown,
          " of ",
          draft.length,
          " shown"
        ] }),
        /* @__PURE__ */ jsxs("span", { className: "flex gap-2", children: [
          /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: reset, children: "Reset" }),
          /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", onClick: () => setOpen(false), children: "Cancel" }),
          /* @__PURE__ */ jsx(Button, { size: "sm", onClick: apply, children: "Apply" })
        ] })
      ] })
    ] })
  ] });
}
function ColumnSettingsRow({
  entry,
  draggable,
  onVisibleChange
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
    disabled: !draggable
  });
  const inputId = `column-pref-${entry.id}`;
  return /* @__PURE__ */ jsxs(
    "li",
    {
      ref: setNodeRef,
      style: { transform: CSS.Transform.toString(transform), transition },
      className: cn(
        "flex items-center gap-2 rounded-md px-1.5 py-1.5 hover:bg-muted/60",
        isDragging && "relative z-10 bg-muted shadow-sm"
      ),
      children: [
        draggable ? /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "cursor-grab touch-none rounded p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:cursor-grabbing",
            "aria-label": `Reorder ${entry.label}`,
            ...attributes,
            ...listeners,
            children: /* @__PURE__ */ jsx(GripVertical, { className: "h-4 w-4" })
          }
        ) : (
          // Keeps the labels on one vertical line whether or not a row has a
          // handle. A locked row that shed the gutter would look like a heading.
          /* @__PURE__ */ jsx("span", { className: "w-5 shrink-0", "aria-hidden": true })
        ),
        /* @__PURE__ */ jsx(
          Checkbox,
          {
            id: inputId,
            checked: entry.visible,
            disabled: entry.alwaysVisible,
            onCheckedChange: onVisibleChange
          }
        ),
        /* @__PURE__ */ jsx(
          "label",
          {
            htmlFor: inputId,
            className: cn(
              "flex-1 cursor-pointer select-none text-sm",
              entry.alwaysVisible && "cursor-default text-muted-foreground"
            ),
            children: entry.label
          }
        ),
        entry.alwaysVisible && /* @__PURE__ */ jsx("span", { className: "text-[10px] uppercase tracking-wide text-muted-foreground", children: "Always" })
      ]
    }
  );
}
function DataTableFacetedFilter({
  column,
  title,
  options,
  defaultOpen
}) {
  const facets = column?.getFacetedUniqueValues();
  const selectedValues = new Set(column?.getFilterValue() ?? []);
  return /* @__PURE__ */ jsxs(Popover, { defaultOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: "h-8 border-dashed", children: [
      /* @__PURE__ */ jsx(PlusCircle, { className: "mr-2 h-4 w-4" }),
      title,
      selectedValues.size > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Separator, { orientation: "vertical", className: "mx-2 h-4" }),
        /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "rounded-sm px-1 font-normal lg:hidden", children: selectedValues.size }),
        /* @__PURE__ */ jsx("div", { className: "hidden space-x-1 lg:flex", children: selectedValues.size > 2 ? /* @__PURE__ */ jsxs(Badge, { variant: "secondary", className: "rounded-sm px-1 font-normal", children: [
          selectedValues.size,
          " selected"
        ] }) : options.filter((option) => selectedValues.has(option.value)).map((option) => /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "rounded-sm px-1 font-normal", children: option.label }, option.value)) })
      ] })
    ] }) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "w-[200px] p-0", align: "start", children: /* @__PURE__ */ jsxs(Command, { children: [
      /* @__PURE__ */ jsx(CommandInput, { placeholder: title }),
      /* @__PURE__ */ jsxs(CommandList, { children: [
        /* @__PURE__ */ jsx(CommandEmpty, { children: "No results found." }),
        /* @__PURE__ */ jsx(CommandGroup, { children: options.map((option) => {
          const isSelected = selectedValues.has(option.value);
          const count = facets?.get(option.value);
          return /* @__PURE__ */ jsxs(
            CommandItem,
            {
              onSelect: () => {
                if (isSelected) selectedValues.delete(option.value);
                else selectedValues.add(option.value);
                const filterValues = Array.from(selectedValues);
                column?.setFilterValue(filterValues.length ? filterValues : void 0);
              },
              children: [
                /* @__PURE__ */ jsx(
                  "div",
                  {
                    className: cn(
                      "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                      isSelected ? "bg-primary text-primary-foreground" : "opacity-50 [&_svg]:invisible"
                    ),
                    children: /* @__PURE__ */ jsx(Check, { className: "h-4 w-4" })
                  }
                ),
                option.icon && /* @__PURE__ */ jsx(option.icon, { className: "mr-2 h-4 w-4 text-muted-foreground" }),
                /* @__PURE__ */ jsx("span", { children: option.label }),
                count !== void 0 && /* @__PURE__ */ jsx("span", { className: "ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs", children: count })
              ]
            },
            option.value
          );
        }) }),
        selectedValues.size > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(CommandSeparator, {}),
          /* @__PURE__ */ jsx(CommandGroup, { children: /* @__PURE__ */ jsx(
            CommandItem,
            {
              onSelect: () => column?.setFilterValue(void 0),
              className: "justify-center text-center",
              children: "Clear filters"
            }
          ) })
        ] })
      ] })
    ] }) })
  ] });
}
var inSelectedSet = (row, columnId, filterValue) => {
  const selected = filterValue;
  if (!selected || selected.length === 0) return true;
  return selected.includes(String(row.getValue(columnId)));
};
inSelectedSet.autoRemove = (value) => !Array.isArray(value) || value.length === 0;
var intersectsSelectedSet = (row, columnId, filterValue) => {
  const selected = filterValue;
  if (!selected || selected.length === 0) return true;
  const values = row.getValue(columnId);
  if (!Array.isArray(values)) return selected.includes(String(values));
  return values.some((value) => selected.includes(String(value)));
};
intersectsSelectedSet.autoRemove = inSelectedSet.autoRemove;
var containsText = (row, columnId, filterValue) => {
  const needle = String(filterValue ?? "").trim().toLowerCase();
  if (!needle) return true;
  const value = row.getValue(columnId);
  if (value == null) return false;
  return String(value).toLowerCase().includes(needle);
};
containsText.autoRemove = (value) => !String(value ?? "").trim();
function isEmptyRange(value) {
  const range = value;
  return range == null || range.min == null && range.max == null;
}
var inNumberRange = (row, columnId, filterValue) => {
  if (isEmptyRange(filterValue)) return true;
  const { min, max } = filterValue;
  const value = toNumber(row.getValue(columnId));
  if (value == null) return false;
  if (min != null && value < min) return false;
  if (max != null && value > max) return false;
  return true;
};
inNumberRange.autoRemove = isEmptyRange;
var inDateRange = (row, columnId, filterValue) => {
  if (isEmptyRange(filterValue)) return true;
  const { min, max } = filterValue;
  const value = toEpochMs(row.getValue(columnId));
  if (value == null) return false;
  if (min != null && value < min) return false;
  if (max != null && value > max + DAY_MS - 1) return false;
  return true;
};
inDateRange.autoRemove = isEmptyRange;
function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : void 0;
  if (typeof value === "bigint") return Number(value);
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : void 0;
  }
  return void 0;
}
function toEpochMs(value) {
  if (value instanceof Date) return value.getTime();
  const numeric = toNumber(value);
  if (numeric != null) return numeric < 1e11 ? numeric * 1e3 : numeric;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? void 0 : parsed;
  }
  return void 0;
}
function resolveFilterFn(spec) {
  switch (spec.kind) {
    case "facet":
      return inSelectedSet;
    case "text":
      return containsText;
    case "number":
      return inNumberRange;
    case "date":
      return inDateRange;
  }
}
function TableFilterBar({
  table,
  columns,
  labels,
  overrides
}) {
  const [opened, setOpened] = React6.useState([]);
  const [justOpened, setJustOpened] = React6.useState(null);
  const filterable = table.getAllLeafColumns().filter((column) => column.columnDef.meta?.filter != null && isOffered(column));
  if (filterable.length === 0) return null;
  const pinned = columns ?? filterable.map((column) => column.id);
  const isShown = (column) => pinned.includes(column.id) || opened.includes(column.id) || // An active filter always shows its own control, however it got set — from a
  // saved view, a deep link, or a chip the reader has since collapsed. The
  // alternative is a table narrowed by something with no control to undo it.
  column.getFilterValue() !== void 0;
  const shown = filterable.filter(isShown);
  const rest = filterable.filter((column) => !isShown(column));
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    shown.map((column) => {
      const spec = withOverride(column.columnDef.meta.filter, overrides?.[column.id]);
      return /* @__PURE__ */ jsx(
        ColumnFilterControl,
        {
          column,
          spec,
          title: spec.title ?? columnLabel(column, labels),
          defaultOpen: justOpened === column.id
        },
        column.id
      );
    }),
    rest.length > 0 && /* @__PURE__ */ jsx(
      AddFilterMenu,
      {
        columns: rest,
        labels,
        overrides,
        onAdd: (id) => {
          requestAnimationFrame(() => {
            setOpened((current) => [...current, id]);
            setJustOpened(id);
          });
        }
      }
    )
  ] });
}
function AddFilterMenu({
  columns,
  labels,
  overrides,
  onAdd
}) {
  const [open, setOpen] = React6.useState(false);
  return /* @__PURE__ */ jsxs(Popover, { open, onOpenChange: setOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsxs(Button, { variant: "ghost", size: "sm", className: "h-8 px-2 text-muted-foreground", children: [
      /* @__PURE__ */ jsx(ListFilter, { className: "mr-1.5 h-4 w-4" }),
      "Filter"
    ] }) }),
    /* @__PURE__ */ jsx(PopoverContent, { className: "w-[220px] p-0", align: "start", children: /* @__PURE__ */ jsxs(Command, { children: [
      /* @__PURE__ */ jsx(CommandInput, { placeholder: "Filter on\u2026" }),
      /* @__PURE__ */ jsxs(CommandList, { children: [
        /* @__PURE__ */ jsx(CommandEmpty, { children: "No more columns to filter." }),
        /* @__PURE__ */ jsx(CommandGroup, { children: columns.map((column) => {
          const spec = withOverride(column.columnDef.meta.filter, overrides?.[column.id]);
          return /* @__PURE__ */ jsx(
            CommandItem,
            {
              value: spec.title ?? columnLabel(column, labels),
              onSelect: () => {
                onAdd(column.id);
                setOpen(false);
              },
              children: spec.title ?? columnLabel(column, labels)
            },
            column.id
          );
        }) })
      ] })
    ] }) })
  ] });
}
function withOverride(spec, override) {
  if (!override) return spec;
  const next = { ...spec };
  if (override.title !== void 0) next.title = override.title;
  if (spec.kind === "facet") {
    if (override.options !== void 0) next.options = override.options;
    if (override.label !== void 0) next.label = override.label;
  }
  return next;
}
function isOffered(column) {
  return column.getIsVisible() || column.getFilterValue() !== void 0;
}
function ColumnFilterControl({
  column,
  spec,
  title,
  defaultOpen
}) {
  switch (spec.kind) {
    case "facet": {
      const selected = column.getFilterValue() ?? [];
      const options = withSelectedOptions(
        spec.options ?? derivedFacetOptions(column, spec.label),
        selected,
        spec.label
      );
      if (options.length < (spec.minOptions ?? 2) && !defaultOpen && selected.length === 0) {
        return null;
      }
      return /* @__PURE__ */ jsx(
        DataTableFacetedFilter,
        {
          column,
          title,
          options,
          defaultOpen
        }
      );
    }
    case "text":
      return /* @__PURE__ */ jsx(
        TextFilter,
        {
          column,
          title,
          placeholder: spec.placeholder,
          defaultOpen
        }
      );
    case "number":
      return /* @__PURE__ */ jsx(
        RangeFilter,
        {
          column,
          title,
          kind: "number",
          step: spec.step,
          unit: spec.unit,
          bounds: spec.bounds !== false,
          defaultOpen
        }
      );
    case "date":
      return /* @__PURE__ */ jsx(RangeFilter, { column, title, kind: "date", defaultOpen });
  }
}
function derivedFacetOptions(column, label) {
  const facets = column.getFacetedUniqueValues();
  if (!facets) return [];
  return [...facets.keys()].filter((value) => typeof value === "string" && value !== "").sort().map((value) => ({ value, label: label ? label(value) : value }));
}
function withSelectedOptions(options, selected, label) {
  const missing = selected.filter((value) => !options.some((option) => option.value === value));
  if (missing.length === 0) return options;
  return [...options, ...missing.map((value) => ({ value, label: label ? label(value) : value }))];
}
function FilterTrigger({
  title,
  summary
}) {
  return /* @__PURE__ */ jsxs(Button, { variant: "outline", size: "sm", className: "h-8 border-dashed", children: [
    /* @__PURE__ */ jsx(PlusCircle, { className: "mr-2 h-4 w-4" }),
    title,
    summary && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Separator, { orientation: "vertical", className: "mx-2 h-4" }),
      /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "rounded-sm px-1 font-normal", children: summary })
    ] })
  ] });
}
function TextFilter({
  column,
  title,
  placeholder,
  defaultOpen
}) {
  const value = column.getFilterValue() ?? "";
  return /* @__PURE__ */ jsxs(Popover, { defaultOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(FilterTrigger, { title, summary: value.trim() || void 0 }) }) }),
    /* @__PURE__ */ jsxs(PopoverContent, { className: "w-[240px] space-y-2 p-3", align: "start", children: [
      /* @__PURE__ */ jsx(
        Input,
        {
          autoFocus: true,
          value,
          placeholder: placeholder ?? `Filter ${title.toLowerCase()}\u2026`,
          onChange: (event) => column.setFilterValue(event.target.value),
          className: "h-8"
        }
      ),
      /* @__PURE__ */ jsx(ClearButton, { onClear: () => column.setFilterValue(void 0), disabled: !value.trim() })
    ] })
  ] });
}
function RangeFilter({
  column,
  title,
  kind,
  step,
  unit,
  bounds: useBounds = true,
  defaultOpen
}) {
  const range = column.getFilterValue() ?? {};
  const bounds = kind === "number" && useBounds ? column.getFacetedMinMaxValues() : void 0;
  const set = (side, raw) => {
    const parsed = raw === "" ? void 0 : kind === "date" ? Date.parse(raw) : Number(raw);
    const next = {
      ...range,
      [side]: parsed != null && Number.isFinite(parsed) ? parsed : void 0
    };
    column.setFilterValue(isEmptyRange(next) ? void 0 : next);
  };
  return /* @__PURE__ */ jsxs(Popover, { defaultOpen, children: [
    /* @__PURE__ */ jsx(PopoverTrigger, { asChild: true, children: /* @__PURE__ */ jsx("span", { children: /* @__PURE__ */ jsx(FilterTrigger, { title, summary: rangeSummary(range, kind, unit) }) }) }),
    /* @__PURE__ */ jsxs(PopoverContent, { className: "w-[300px] space-y-2 p-3", align: "start", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-end gap-2", children: [
        /* @__PURE__ */ jsx(
          RangeInput,
          {
            label: "From",
            kind,
            step,
            value: range.min,
            placeholder: bounds?.[0] != null ? String(bounds[0]) : void 0,
            onChange: (raw) => set("min", raw)
          }
        ),
        /* @__PURE__ */ jsx(
          RangeInput,
          {
            label: "To",
            kind,
            step,
            value: range.max,
            placeholder: bounds?.[1] != null ? String(bounds[1]) : void 0,
            onChange: (raw) => set("max", raw)
          }
        )
      ] }),
      /* @__PURE__ */ jsx(
        ClearButton,
        {
          onClear: () => column.setFilterValue(void 0),
          disabled: isEmptyRange(range)
        }
      )
    ] })
  ] });
}
function RangeInput({
  label,
  kind,
  step,
  value,
  placeholder,
  onChange
}) {
  const id = React6.useId();
  return /* @__PURE__ */ jsxs("div", { className: "flex-1 space-y-1", children: [
    /* @__PURE__ */ jsx(Label, { htmlFor: id, className: "text-xs text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx(
      Input,
      {
        id,
        type: kind === "date" ? "date" : "number",
        step,
        value: value == null ? "" : kind === "date" ? toDateInput(value) : String(value),
        placeholder,
        onChange: (event) => onChange(event.target.value),
        className: "h-8"
      }
    )
  ] });
}
function ClearButton({
  onClear,
  disabled
}) {
  return /* @__PURE__ */ jsx(
    Button,
    {
      variant: "ghost",
      size: "sm",
      onClick: onClear,
      disabled,
      className: cn("h-7 w-full text-xs", disabled && "invisible"),
      children: "Clear"
    }
  );
}
function rangeSummary(range, kind, unit) {
  if (isEmptyRange(range)) return void 0;
  const show = (value) => value == null ? "" : kind === "date" ? toDateInput(value) : `${value}${unit ?? ""}`;
  if (range.min != null && range.max != null) return `${show(range.min)} \u2013 ${show(range.max)}`;
  return range.min != null ? `\u2265 ${show(range.min)}` : `\u2264 ${show(range.max)}`;
}
function toDateInput(epochMs) {
  const date = new Date(epochMs);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

// src/table/searchBox.ts
function searchBoxMode(args) {
  if (args.hasServerSearch) return "server";
  if (args.hasSearchColumn) return "column";
  return args.globalSearch ? "global" : "none";
}
function isTableFiltered(args) {
  return args.columnFilterCount > 0 || args.globalTerm !== "" || args.serverTerm !== "";
}

// src/table/loadedRows.ts
function isClientFiltered(args) {
  return args.columnFilterCount > 0 || args.globalTerm.trim() !== "";
}
function loadedRowsNote(args) {
  if (!args.hasUnloadedRows) return null;
  const verbs = [args.filtered && "filtered", args.sorted && "sorted"].filter(Boolean);
  return verbs.length > 0 ? `${verbs.join(" and ")} within the loaded rows` : null;
}
function isFilteredOut(args) {
  return args.loadedCount > 0 && isClientFiltered(args);
}
function DataTableToolbar({
  table,
  searchColumn,
  globalSearch = false,
  serverSearch,
  searchPlaceholder = "Search\u2026",
  filterColumns,
  filterOverrides,
  children,
  viewOptionLabels,
  enableColumnReorder = true,
  defaultColumnVisibility,
  hasUnloadedRows = false,
  className
}) {
  const state = table.getState();
  const globalTerm = state.globalFilter ?? "";
  const searchColumnRef = searchColumn ? table.getColumn(searchColumn) : void 0;
  const mode = searchBoxMode({
    hasServerSearch: serverSearch != null,
    hasSearchColumn: searchColumnRef != null,
    globalSearch
  });
  const showSearch = mode !== "none";
  const isFiltered = isTableFiltered({
    columnFilterCount: state.columnFilters.length,
    globalTerm,
    serverTerm: serverSearch?.value ?? ""
  });
  const loadedNote = loadedRowsNote({
    hasUnloadedRows,
    filtered: isClientFiltered({ columnFilterCount: state.columnFilters.length, globalTerm }),
    sorted: state.sorting.length > 0
  });
  const term = mode === "server" ? serverSearch?.value ?? "" : mode === "column" ? searchColumnRef?.getFilterValue() ?? "" : globalTerm;
  const setTerm = (value) => {
    if (mode === "server") return serverSearch?.onChange(value);
    if (mode === "column") return searchColumnRef?.setFilterValue(value);
    table.setGlobalFilter(value);
  };
  return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center justify-between gap-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-1 flex-wrap items-center gap-2", children: [
      showSearch && /* @__PURE__ */ jsx(
        Input,
        {
          placeholder: searchPlaceholder,
          value: term,
          onChange: (event) => setTerm(event.target.value),
          className: "h-8 w-[150px] lg:w-[250px]"
        }
      ),
      /* @__PURE__ */ jsx(
        TableFilterBar,
        {
          table,
          ...filterColumns ? { columns: filterColumns } : {},
          ...viewOptionLabels ? { labels: viewOptionLabels } : {},
          ...filterOverrides ? { overrides: filterOverrides } : {}
        }
      ),
      children,
      isFiltered && /* @__PURE__ */ jsxs(
        Button,
        {
          variant: "ghost",
          size: "sm",
          onClick: () => {
            table.resetColumnFilters();
            table.setGlobalFilter("");
            serverSearch?.onChange("");
          },
          className: "h-8 px-2 lg:px-3",
          children: [
            "Reset",
            /* @__PURE__ */ jsx(X, { className: "ml-2 h-4 w-4" })
          ]
        }
      ),
      loadedNote && /* @__PURE__ */ jsx(
        "span",
        {
          className: "text-xs text-muted-foreground",
          title: "More rows are available upstream, and this only covers the rows already loaded. Load more to include the rest.",
          children: loadedNote
        }
      )
    ] }),
    /* @__PURE__ */ jsx(
      TableColumnSettings,
      {
        table,
        ...viewOptionLabels ? { labels: viewOptionLabels } : {},
        enableReorder: enableColumnReorder,
        ...defaultColumnVisibility ? { defaultVisibility: defaultColumnVisibility } : {}
      }
    )
  ] });
}
function TableSelectionBar({
  selected,
  clearSelection,
  actions,
  className
}) {
  if (selected.length === 0) return null;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2 text-sm",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
          selected.length,
          " selected"
        ] }),
        /* @__PURE__ */ jsx("div", { className: "ml-auto flex items-center gap-2", children: actions(selected, { clearSelection }) })
      ]
    }
  );
}
function matchesSearchTerm(term, columns, valueOf) {
  const needle = term.trim().toLowerCase();
  if (!needle) return true;
  return columns.some((id) => {
    const value = valueOf(id);
    if (value == null) return false;
    return String(value).toLowerCase().includes(needle);
  });
}
function useControllable(controlled, onChange, initial) {
  const [internal, setInternal] = React6.useState(initial);
  const isControlled = controlled !== void 0;
  const value = isControlled ? controlled : internal;
  const set = (updater) => {
    if (!isControlled) setInternal(updater);
    onChange?.(updater);
  };
  return [value, set];
}
function withDeclaredFilterFns(columns) {
  return columns.map((column) => {
    const nested = column.columns;
    if (nested?.length) return { ...column, columns: withDeclaredFilterFns(nested) };
    const spec = column.meta?.filter;
    if (!spec || column.filterFn) return column;
    return { ...column, filterFn: resolveFilterFn(spec) };
  });
}
function useDataTable({
  data,
  columns,
  pageSize = 20,
  enableRowSelection = true,
  initialSorting = [],
  initialColumnVisibility = {},
  columnVisibility: controlledVisibility,
  onColumnVisibilityChange,
  initialColumnFilters = [],
  columnFilters: controlledFilters,
  onColumnFiltersChange,
  sorting: controlledSorting,
  onSortingChange,
  initialColumnOrder = [],
  columnOrder: controlledOrder,
  onColumnOrderChange,
  enableColumnResizing = false,
  initialColumnSizing = {},
  columnSizing: controlledSizing,
  onColumnSizingChange,
  searchColumns,
  tableOptions
}) {
  const [rowSelection, setRowSelection] = React6.useState({});
  const [globalFilter, setGlobalFilter] = React6.useState("");
  const [pagination, setPagination] = React6.useState({ pageIndex: 0, pageSize });
  const [columnVisibility, handleVisibilityChange] = useControllable(
    controlledVisibility,
    onColumnVisibilityChange,
    initialColumnVisibility
  );
  const [columnFilters, handleFiltersChange] = useControllable(
    controlledFilters,
    onColumnFiltersChange,
    initialColumnFilters
  );
  const [sorting, handleSortingChange] = useControllable(
    controlledSorting,
    onSortingChange,
    initialSorting
  );
  const [columnOrder, handleOrderChange] = useControllable(
    controlledOrder,
    onColumnOrderChange,
    initialColumnOrder
  );
  const [columnSizing, handleSizingChange] = useControllable(
    controlledSizing,
    onColumnSizingChange,
    initialColumnSizing
  );
  const resolvedColumns = React6.useMemo(
    () => withDeclaredFilterFns(columns),
    [columns]
  );
  const globalSearch = searchColumns?.length ? {
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: (row, _columnId, value) => matchesSearchTerm(value, searchColumns, (id) => row.getValue(id))
  } : void 0;
  return useReactTable({
    data,
    columns: resolvedColumns,
    ...globalSearch,
    state: {
      sorting,
      columnVisibility,
      columnOrder,
      rowSelection,
      columnFilters,
      pagination,
      columnSizing,
      ...globalSearch ? { globalFilter } : {}
    },
    enableRowSelection,
    enableColumnResizing,
    // Resize live rather than on release: the body is `table-layout: fixed`, so
    // the cells track the header as it moves and the reader can see what a width
    // will actually hold before letting go.
    columnResizeMode: "onChange",
    onRowSelectionChange: setRowSelection,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleFiltersChange,
    onColumnVisibilityChange: handleVisibilityChange,
    onColumnOrderChange: handleOrderChange,
    onColumnSizingChange: handleSizingChange,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    // For the range controls: a number/date filter opens on the column's actual
    // bounds, so the reader is nudging a real interval rather than guessing at
    // one. Cheap — it is computed lazily, only for a column that asks.
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    getPaginationRowModel: getPaginationRowModel(),
    ...tableOptions
  });
}
function stopRowClick(e) {
  e.stopPropagation();
}
function selectionColumn() {
  return {
    id: "select",
    header: ({ table }) => /* @__PURE__ */ jsx(
      Checkbox,
      {
        checked: table.getIsAllPageRowsSelected(),
        indeterminate: table.getIsSomePageRowsSelected() && !table.getIsAllPageRowsSelected(),
        onCheckedChange: (value) => table.toggleAllPageRowsSelected(value),
        onClick: stopRowClick,
        "aria-label": "Select all rows"
      }
    ),
    cell: ({ row }) => /* @__PURE__ */ jsx(
      Checkbox,
      {
        checked: row.getIsSelected(),
        disabled: !row.getCanSelect(),
        onCheckedChange: (value) => row.toggleSelected(value),
        onClick: stopRowClick,
        "aria-label": "Select row"
      }
    ),
    enableSorting: false,
    enableHiding: false,
    // Its position is structural, not a preference: a column of ticks anywhere
    // but the leading edge reads as data. The settings dialog draws it without a
    // drag handle, and `reconcileColumnOrder` puts it back at the front if a
    // stored order — one saved before this line existed, or shared by a
    // teammate — claims otherwise.
    meta: { lockPosition: true, label: "Select" },
    size: 32
  };
}
function withSelectionColumn(columns, enabled) {
  const present = columns.some(
    (column) => (column.id ?? column.accessorKey) === "select"
  );
  if (!enabled || present) return columns;
  return [selectionColumn(), ...columns];
}
var VISIBILITY_NAME = "table:cols";
var WIDTHS_NAME = "table:widths";
var ORDER_NAME = "table:order";
var identity = {};
var scope = null;
function store() {
  scope ??= createScopedStorage(identity);
  return scope;
}
function sameIdentity(a, b) {
  return (a.app ?? null) === (b.app ?? null) && (a.customerId ?? null) === (b.customerId ?? null) && (a.orgId ?? null) === (b.orgId ?? null) && (a.tenantId ?? null) === (b.tenantId ?? null) && (a.userId ?? null) === (b.userId ?? null);
}
function setColumnPrefsIdentity(next) {
  if (sameIdentity(identity, next)) return;
  identity = { ...next };
  scope = null;
}
function columnPrefsIdentity() {
  return { ...identity };
}
function readPref(name, viewKey) {
  return store().get(`${name}:${viewKey}`, void 0);
}
function readInitialColumnVisibility(viewKey, fallback = {}) {
  if (!viewKey) return fallback;
  const stored = readPref(VISIBILITY_NAME, viewKey);
  return stored ? { ...fallback, ...stored } : fallback;
}
function readInitialColumnWidths(viewKey) {
  if (!viewKey) return {};
  return readPref(WIDTHS_NAME, viewKey) ?? {};
}
function readInitialColumnOrder(viewKey, columns) {
  if (!viewKey) return [];
  const stored = readPref(ORDER_NAME, viewKey);
  if (!stored?.length) return [];
  return reconcileColumnOrder(stored, columns);
}
function reconcileColumnOrder(order, columns) {
  const present = columnIds(columns);
  const known = new Set(present);
  const locked = present.filter((id) => isLocked(id, columns));
  const lockedSet = new Set(locked);
  const seen = /* @__PURE__ */ new Set();
  const kept = [];
  for (const id of order) {
    if (!known.has(id) || lockedSet.has(id) || seen.has(id)) continue;
    seen.add(id);
    kept.push(id);
  }
  const appended = present.filter((id) => !lockedSet.has(id) && !seen.has(id));
  return [...locked, ...kept, ...appended];
}
function isLocked(id, columns) {
  return columns.some((column) => columnKey(column) === id && column.meta?.lockPosition === true);
}
function withPinnedColumnsVisible(visibility, columns) {
  const pinned = columns.filter((c) => c.enableHiding === false).map((c) => columnKey(c));
  if (!pinned.some((id) => id !== void 0 && visibility[id] === false)) return visibility;
  const repaired = { ...visibility };
  for (const id of pinned) if (id !== void 0) delete repaired[id];
  return repaired;
}
function columnKey(column) {
  if (column.id) return column.id;
  const key = column.accessorKey;
  return typeof key === "string" ? key : void 0;
}
function columnIds(columns) {
  return columns.map((c) => columnKey(c)).filter((id) => id !== void 0);
}
function usePersistColumnVisibility(table, viewKey) {
  usePersistOnChange(viewKey, VISIBILITY_NAME, table.getState().columnVisibility);
}
function usePersistColumnWidths(table, viewKey) {
  usePersistOnChange(viewKey, WIDTHS_NAME, table.getState().columnSizing);
}
function usePersistColumnOrder(table, viewKey) {
  usePersistOnChange(viewKey, ORDER_NAME, table.getState().columnOrder);
}
function usePersistOnChange(viewKey, name, value) {
  const serialized = JSON.stringify(value);
  const initial = React6.useRef(serialized);
  const changed = React6.useRef(false);
  React6.useEffect(() => {
    if (!viewKey) return;
    if (!changed.current) {
      if (serialized === initial.current) return;
      changed.current = true;
    }
    store().set(`${name}:${viewKey}`, JSON.parse(serialized));
  }, [viewKey, name, serialized]);
}
function renderSlot(slot, table) {
  return typeof slot === "function" ? slot(table) : slot;
}
var CLIENT_PAGE_SIZE = 1e5;
var EMPTY_ROWS = [];
var EMPTY_COLUMNS = [];
function ResourceTable({
  rows,
  columns,
  getRowId,
  isLoading = false,
  isFetching = false,
  error,
  onRetry,
  toolbar,
  facets,
  toolbarActions,
  banner,
  selectionActions,
  pagination,
  viewKey,
  defaultColumnVisibility,
  columnVisibility,
  onColumnVisibilityChange,
  initialColumnFilters,
  columnFilters,
  onColumnFiltersChange,
  sorting,
  onSortingChange,
  initialSorting,
  columnOrder,
  onColumnOrderChange,
  enableColumnReorder = true,
  columnSizing,
  onColumnSizingChange,
  initialColumnSizing,
  enableRowSelection = false,
  enableColumnResizing = false,
  density,
  rowHeight = 44,
  height = "h-[600px]",
  activeRowId,
  rowClassName,
  onEndReached,
  onRowClick,
  renderRowDetail,
  detailTitle,
  detailEyebrow,
  detailSubtitle,
  detailFooter,
  emptyTitle = "Nothing to show",
  emptyDescription = "There is nothing to show here yet.",
  emptyAction,
  className,
  table: controlledTable,
  onTableReady
}) {
  const rowIdOption = React6.useMemo(
    () => getRowId ? { getRowId: (row, index) => getRowId(row) ?? `idx:${index}` } : {},
    [getRowId]
  );
  const toolbarSuppressed = toolbar === false;
  const toolbarConfig = toolbar === true ? {} : toolbarSuppressed || toolbar == null ? void 0 : toolbar;
  const ownedViewKey = controlledTable ? void 0 : viewKey;
  const storedVisibility = React6.useMemo(
    () => readInitialColumnVisibility(ownedViewKey, defaultColumnVisibility),
    // The surface default seeds the first render only; re-reading it on a new
    // object identity would look reactive without being so.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownedViewKey]
  );
  const storedSizing = React6.useMemo(
    () => ({ ...readInitialColumnWidths(ownedViewKey), ...initialColumnSizing }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownedViewKey]
  );
  const resolvedColumns = React6.useMemo(
    () => withSelectionColumn(columns, enableRowSelection),
    [columns, enableRowSelection]
  );
  const resolvedVisibility = React6.useMemo(
    () => columnVisibility ? withPinnedColumnsVisible(columnVisibility, resolvedColumns) : void 0,
    [columnVisibility, resolvedColumns]
  );
  const storedOrder = React6.useMemo(
    () => readInitialColumnOrder(ownedViewKey, resolvedColumns),
    // The stored value seeds the first render only; re-reading it whenever the
    // column array is rebuilt would look reactive without being so.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownedViewKey]
  );
  const internalTable = useDataTable({
    data: controlledTable ? EMPTY_ROWS : rows,
    columns: controlledTable ? EMPTY_COLUMNS : resolvedColumns,
    pageSize: CLIENT_PAGE_SIZE,
    enableRowSelection,
    enableColumnResizing,
    initialSorting: initialSorting ?? [],
    initialColumnVisibility: storedVisibility,
    initialColumnSizing: storedSizing,
    columnVisibility: resolvedVisibility,
    onColumnVisibilityChange,
    initialColumnFilters: initialColumnFilters ?? [],
    columnFilters,
    onColumnFiltersChange,
    sorting,
    onSortingChange,
    initialColumnOrder: storedOrder,
    columnOrder,
    onColumnOrderChange,
    columnSizing,
    onColumnSizingChange,
    ...toolbarConfig?.searchColumns ? { searchColumns: toolbarConfig.searchColumns } : {},
    tableOptions: rowIdOption
  });
  const table = controlledTable ?? internalTable;
  usePersistColumnVisibility(internalTable, ownedViewKey);
  usePersistColumnWidths(internalTable, ownedViewKey);
  usePersistColumnOrder(internalTable, ownedViewKey);
  React6.useEffect(() => {
    onTableReady?.(table);
  }, [onTableReady, table]);
  const [detailRow, setDetailRow] = React6.useState(null);
  const handleRowClick = React6.useCallback(
    (row) => {
      onRowClick?.(row);
      if (renderRowDetail) setDetailRow(row);
    },
    [onRowClick, renderRowDetail]
  );
  const rowClickHandler = onRowClick || renderRowDetail ? handleRowClick : void 0;
  const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original);
  const clearSelection = React6.useCallback(() => table.resetRowSelection(), [table]);
  const tableState = table.getState();
  const filteredOut = isFilteredOut({
    loadedCount: table.getCoreRowModel().rows.length,
    columnFilterCount: tableState.columnFilters.length,
    globalTerm: tableState.globalFilter ?? ""
  });
  const moreUpstream = pagination?.hasNext === true;
  const emptyState = filteredOut ? /* @__PURE__ */ jsx(
    EmptyState,
    {
      title: moreUpstream ? "No loaded rows match these filters" : "No rows match these filters",
      description: moreUpstream ? "Only the rows loaded so far were checked, and more are available. Load more to keep looking, or clear the filters." : "Clear a filter to see rows again.",
      action: /* @__PURE__ */ jsxs("span", { className: "flex flex-wrap justify-center gap-2", children: [
        /* @__PURE__ */ jsx(
          Button,
          {
            variant: "outline",
            size: "sm",
            onClick: () => {
              table.resetColumnFilters(true);
              table.setGlobalFilter("");
            },
            children: "Clear filters"
          }
        ),
        moreUpstream && pagination?.onNext && // Here rather than only in the footer pager: an empty body renders no
        // rows, so scrolling can never reach the end and auto-load the next
        // page — this and the pager are the only ways forward.
        /* @__PURE__ */ jsx(Button, { size: "sm", onClick: pagination.onNext, disabled: isFetching, children: isFetching ? "Loading\u2026" : "Load more" })
      ] })
    }
  ) : /* @__PURE__ */ jsx(EmptyState, { title: emptyTitle, description: emptyDescription, action: emptyAction });
  const effectiveRowHeight = density ? TABLE_DENSITY_ROW_HEIGHT[density] : rowHeight;
  const showToolbar = !toolbarSuppressed && (toolbarConfig !== void 0 || facets != null || toolbarActions != null);
  if (error && !showToolbar) {
    return /* @__PURE__ */ jsx(ErrorState, { error, onRetry, className });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    showToolbar && /* @__PURE__ */ jsxs(
      DataTableToolbar,
      {
        table,
        searchColumn: toolbarConfig?.searchColumn,
        globalSearch: Boolean(toolbarConfig?.searchColumns?.length),
        serverSearch: toolbarConfig?.serverSearch,
        searchPlaceholder: toolbarConfig?.searchPlaceholder,
        viewOptionLabels: toolbarConfig?.columnLabels,
        ...toolbarConfig?.filterColumns ? { filterColumns: toolbarConfig.filterColumns } : {},
        ...toolbarConfig?.filterOverrides ? { filterOverrides: toolbarConfig.filterOverrides } : {},
        enableColumnReorder,
        ...defaultColumnVisibility ? { defaultColumnVisibility } : {},
        hasUnloadedRows: pagination?.hasNext === true,
        children: [
          renderSlot(facets, table),
          renderSlot(toolbarActions, table)
        ]
      }
    ),
    renderSlot(banner, table),
    selectionActions && /* @__PURE__ */ jsx(
      TableSelectionBar,
      {
        selected: selectedRows,
        clearSelection,
        actions: selectionActions
      }
    ),
    error ? /* @__PURE__ */ jsx(ErrorState, { error, onRetry }) : isLoading && rows.length === 0 ? /* @__PURE__ */ jsx("div", { className: cn("space-y-2 rounded-md border p-3", height), children: Array.from({ length: 8 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-8 w-full" }, i)) }) : /* @__PURE__ */ jsx(
      DataTable,
      {
        table,
        rowHeight: effectiveRowHeight,
        className: height,
        onRowClick: rowClickHandler,
        getRowId,
        activeRowId,
        rowClassName,
        ...onEndReached ? { onEndReached } : {},
        emptyState
      }
    ),
    pagination && !error && /* @__PURE__ */ jsx(
      DataTablePagination,
      {
        hasPrev: pagination.hasPrev,
        hasNext: pagination.hasNext,
        onPrev: pagination.onPrev,
        onNext: pagination.onNext,
        label: pagination.label,
        isLoading: isFetching
      }
    ),
    renderRowDetail && /* @__PURE__ */ jsx(
      DrilldownPanel,
      {
        open: detailRow != null,
        onOpenChange: (open) => !open && setDetailRow(null),
        eyebrow: detailEyebrow,
        title: detailRow ? detailTitle?.(detailRow) ?? getRowId?.(detailRow) ?? "" : "",
        subtitle: detailRow ? detailSubtitle?.(detailRow) : void 0,
        footer: detailRow ? detailFooter?.(detailRow) : void 0,
        children: detailRow && renderRowDetail(detailRow)
      }
    )
  ] });
}

export { DataTable, DataTableColumnHeader, DataTableFacetedFilter, DataTablePagination, DataTableToolbar, FLEX_COLUMN_MIN_WIDTH, ResourceTable, TableColumnSettings, TableFilterBar, TableSelectionBar, columnIds, columnLabel, columnPrefsIdentity, containsText, declaredMinSizes, declaredSizeIds, inDateRange, inNumberRange, inSelectedSet, intersectsSelectedSet, isEmptyRange, isNearEnd, isOwnControlEvent, matchesSearchTerm, readInitialColumnOrder, readInitialColumnVisibility, readInitialColumnWidths, reconcileColumnOrder, renderSlot, resolveFilterFn, selectionColumn, setColumnPrefsIdentity, useDataTable, usePersistColumnOrder, usePersistColumnVisibility, usePersistColumnWidths, withPinnedColumnsVisible, withSelectionColumn };
//# sourceMappingURL=chunk-GJXCCBTP.js.map
//# sourceMappingURL=chunk-GJXCCBTP.js.map